import React from "react";
import {
  DataTable,
  TableBody,
  TableCaption,
  TableCell,
  TableRow,
  TableRowHeader,
  theme,
} from "@ovotech/element";
import styled from "styled-components";
import { TariffInformationLabel } from "../../models/TariffInformationLabel";
import { Fuel } from "../../models/Fuel";

const SubHeading = styled.span`
  font-weight: normal;
`;

function TableHeaderWithSubHeading({
  heading,
  subHeading,
  useDiv = false,
}: {
  heading: string;
  subHeading: string;
  useDiv?: boolean;
}) {
  return useDiv ? (
    <div>
      <span>{heading}</span>
      <span className="table-sub-heading"> {subHeading}</span>
    </div>
  ) : (
    <TableRowHeader>
      <span>{heading}</span>
      <SubHeading> {subHeading}</SubHeading>
    </TableRowHeader>
  );
}

const StyledCaption = styled(TableCaption)`
  padding-left: ${theme.space["3"]};
  border-bottom: solid 1px ${theme.colors.border};
`;

const StyledDataTable = styled(DataTable)`
  margin-bottom: ${theme.space["5"]};
`;

interface RowData {
  subHeading: string;
  value: string | number;
}

interface TableDefinition {
  "Payment method": RowData;
  "Meter type": RowData;
  "Unit rate": RowData;
  "Night unit rate"?: RowData;
  "Standing charge": RowData;
  "Exit fee": RowData;
}

function unitRateDisplay(input: number | undefined): string {
  return input ? (input * 100).toFixed(2) : "N/A";
}

function FuelTable({ til }: { til: TariffInformationLabel }) {
  const rows: TableDefinition = {
    "Payment method": {
      subHeading: "",
      value: til.paymentMethod
        .split("_")
        .map((s) => s.charAt(0) + s.slice(1).toLowerCase())
        .join(" "),
    },
    "Meter type": {
      subHeading: "",
      value: til.profileClass == 1 ? "Standard" : " Multi-rate",
    },
    "Unit rate": {
      subHeading: "Pence per kWh",
      value: unitRateDisplay(til.ratesByTimingCategory.find(r => ["PEAK", "ANYTIME"].includes(r.timingCategory.toUpperCase()))?.unitRate)
    },
    "Night unit rate": {
      subHeading: "Pence per kWh",
      value: unitRateDisplay(til.ratesByTimingCategory.find(r => "OFFPEAK" === r.timingCategory.toUpperCase())?.unitRate)
    },
    "Standing charge": {
      subHeading: "Pence per day",
      value: unitRateDisplay(til.standingCharge),
    },
    "Exit fee": {
      subHeading: "",
      value: `£${til.exitFee}`,
    },
  };

  if (til.fuel === Fuel.GAS) {
    delete rows["Night unit rate"];
  }

  return (
    <StyledDataTable>
      <StyledCaption>{til.fuel}</StyledCaption>
      <TableBody>
        {Object.entries(rows).map(([name, data], index) => (
          <TableRow key={index} stripe={!!(index % 2)}>
            <TableHeaderWithSubHeading
              heading={name}
              subHeading={data.subHeading}
            />
            <TableCell>{data.value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </StyledDataTable>
  );
}

export default FuelTable;
